/**
 * Payroll Archive tab — split out of pages/Payroll.jsx (2026-02).
 *
 * Contains ArchiveTab + its two helpers MiniKpi + ReadonlyPayrollSummary.
 * Read-only summary of any past finalized payroll run, plus the
 * super-user unlock/amend flow that hops back to Current Run.
 * Zero behaviour change from the pre-split inline definitions.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { fmtDate } from '../../lib/date';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import {
  Archive, Printer, Lock, Unlock, Shield, ArrowLeft, Search, AlertTriangle,
} from 'lucide-react';
import {
  C, usd, usdShort, pct, int,
  sortAndGroupRows,
} from '../../lib/payrollHelpers';
import { DetailTable, UnlockDialog, ValidationBlock, KpiRow } from './CurrentRun';

function ArchiveTab({ initialRunId, isSuper = false, onAmended }) {
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(initialRunId || null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendReason, setAmendReason] = useState('');
  const [amending, setAmending] = useState(false);

  useEffect(() => {
    api.get('/payroll/runs', { params: { limit: 200, status: 'finalized' } }).then(({ data }) => {
      setRuns(data.runs || []);
      if (!selected && data.runs?.length) setSelected(data.runs[0].run_id);
    }).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/payroll/runs/${selected}`).then(({ data }) => setDetail(data)).catch(() => setDetail(null));
  }, [selected]);

  const grand = useMemo(() => {
    const rows = detail?.rows || [];
    return rows.reduce((acc, r) => ({
      show: acc.show + (r.show_units || 0),
      lc: acc.lc + (r.lc_units || 0),
      ns: acc.ns + (r.ns_units || 0),
      canceled: acc.canceled + (r.canceled_units || 0),
      total: acc.total + (r.total_appts || 0),
      adj: acc.adj + (r.session_adjustment || 0),
      payable: acc.payable + (r.payable_units || 0),
      lcns: acc.lcns + (r.lc_ns_comp_cents || 0),
      bonus: acc.bonus + (r.bonus_amount_cents || 0),
      cpt507: acc.cpt507 + (r.cpt_92507_units || 0),
      cptEval: acc.cptEval + ((r.cpt_92523_units || 0) + (r.cpt_92610_units || 0)),
      cpt526: acc.cpt526 + (r.cpt_92526_units || 0),
      therapy: acc.therapy + (r.therapy_comp_cents || 0),
      evalc:   acc.evalc   + (r.eval_comp_cents || 0),
    }), { show: 0, lc: 0, ns: 0, canceled: 0, total: 0, adj: 0, payable: 0, lcns: 0, bonus: 0, cpt507: 0, cptEval: 0, cpt526: 0, therapy: 0, evalc: 0 });
  }, [detail]);

  if (loading) return <div className="p-8 text-center" style={{ color: C.soft }}>Loading archive…</div>;
  const broken = (detail?.rows || []).filter(r => r.data_broken).length;
  return (
    <div className="grid md:grid-cols-[240px_1fr] gap-4" data-testid="pr-archive">
      <Card className="p-2 border" style={{ borderColor: C.border }} data-testid="pr-archive-list">
        <div className="text-[10px] uppercase tracking-widest font-bold px-2 pt-1 pb-2" style={{ color: C.gold }}>Finalized periods</div>
        <div className="max-h-[600px] overflow-auto">
          {runs.map(r => (
            <button key={r.run_id}
                    onClick={() => setSelected(r.run_id)}
                    data-testid={`pr-archive-item-${r.run_id}`}
                    className={`w-full text-left px-3 py-2 rounded-md text-[13px] my-0.5 ${
                      selected === r.run_id ? 'bg-[#EFE9F5] text-[#2D2833] font-semibold' : 'hover:bg-[#F6F2FA] text-[#4A3B5F]'
                    }`}>
              {r.period_start} → {r.period_end}
              <div className="text-[10px]" style={{ color: C.soft }}>
                {r.detail_preserved === false ? 'KPI-only (legacy)' : `${r.validation?.show_units ?? '—'} show units`}
              </div>
            </button>
          ))}
          {runs.length === 0 && <div className="text-[12px] px-3 py-4" style={{ color: C.soft }}>No finalized runs yet.</div>}
        </div>
      </Card>
      <div>
        {!detail ? (
          <Card className="p-8 border text-center" style={{ borderColor: C.border, color: C.soft }}>Select a period.</Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.gold }}>Finalized period</div>
                <div className="font-serif text-lg" style={{ color: C.ink }}>{detail.run.period_start} → {detail.run.period_end}</div>
                <div className="text-[11px]" style={{ color: C.soft }}>
                  Finalized {fmtDate(detail.run.finalized_at)} by {detail.run.finalized_by}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isSuper && (
                  <Button
                    variant="outline"
                    onClick={() => { setAmendReason(''); setAmendOpen(true); }}
                    data-testid="pr-archive-amend"
                    className="border-[#8A3A3A] text-[#8A3A3A] hover:bg-[#F5EAEA]"
                  >
                    <Unlock size={12} className="mr-1" /> Amend
                  </Button>
                )}
                <Button variant="outline" onClick={() => window.print()} data-testid="pr-archive-print">
                  <Printer size={12} className="mr-1" /> Print
                </Button>
              </div>
            </div>
            {broken > 0 && (
              <Card className="p-3 border-l-4 border" style={{ borderColor: C.rose, borderLeftColor: C.rose, background: '#FDECEA' }} data-testid="pr-broken-banner">
                <div className="flex items-center gap-2 text-[12px]" style={{ color: C.rose }}>
                  <AlertTriangle size={13} />
                  {broken} row(s) with unrecoverable legacy values (#REF!). Compensation and bonus dollars are shown as $0
                  because the original Setup rows were deleted before this period was archived — not because they earned $0.
                </div>
              </Card>
            )}
            {detail.run.detail_preserved === false ? (
              <Card className="p-6 border" style={{ borderColor: C.border }} data-testid="pr-archive-kpi-only">
                <div className="text-[12px]" style={{ color: C.soft }}>Per-therapist detail was not preserved for this legacy period. Only KPI totals are available.</div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MiniKpi label="Income" v={usdShort(detail.summary?.sp_income_cents)} />
                  <MiniKpi label="Payroll" v={usdShort(detail.summary?.total_payroll_cents)} />
                  <MiniKpi label="Margin" v={usdShort(detail.summary?.margin_cents)} />
                  <MiniKpi label="Margin %" v={pct(detail.summary?.margin_pct || 0)} />
                </div>
              </Card>
            ) : (
              <>
                <ValidationBlock run={detail.run} rows={detail.rows} />
                <DetailTable rows={sortAndGroupRows(detail.rows)} finalized onAdjust={() => {}} onProcess={() => {}} onRequestUnlock={null} grand={grand} />
                {detail.summary && (
                  <ReadonlyPayrollSummary summary={detail.summary} />
                )}
              </>
            )}
          </div>
        )}
      </div>
      {/* Amend dialog (iter 220): super_user only. Unlocks the finalized
          run back to draft state, appends override_history for audit,
          then routes the user to Current Run tab with the run_id in
          the URL so corrections can begin immediately. */}
      <Dialog open={amendOpen} onOpenChange={(v) => !v && setAmendOpen(false)}>
        <DialogContent data-testid="pr-amend-dialog">
          <DialogHeader>
            <DialogTitle>Amend finalized run</DialogTitle>
          </DialogHeader>
          <div className="text-[12px]" style={{ color: C.soft }}>
            {selected && detail?.run && (
              <>Reopen <b>{detail.run.period_start} → {detail.run.period_end}</b> for corrections. The run will move back to draft and this action will be recorded in the audit trail.</>
            )}
          </div>
          <div className="mt-3">
            <Label className="text-[11px]" style={{ color: C.soft }}>Reason (required)</Label>
            <Textarea
              value={amendReason}
              onChange={(e) => setAmendReason(e.target.value)}
              rows={3}
              placeholder="e.g. Corrected LC/NS units for Jane Smith — attendance file re-imported after payer denial reversal"
              data-testid="pr-amend-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAmendOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!amendReason.trim()) { toast.error('Reason is required for audit'); return; }
                setAmending(true);
                try {
                  await api.post(`/payroll/runs/${selected}/override`, { reason: amendReason.trim() });
                  toast.success('Run reopened for amendment');
                  setAmendOpen(false);
                  onAmended?.(selected);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Amend failed');
                } finally {
                  setAmending(false);
                }
              }}
              disabled={amending || !amendReason.trim()}
              className="bg-[#8A3A3A] hover:bg-[#6E2E2E] text-white"
              data-testid="pr-amend-submit"
            >
              <Unlock size={12} className="mr-1" /> Unlock for amendment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniKpi({ label, v }) {
  return (
    <div className="rounded-xl border p-3" style={{ borderColor: C.border }}>
      <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>{label}</div>
      <div className="font-serif text-xl" style={{ color: C.ink }}>{v}</div>
    </div>
  );
}

function ReadonlyPayrollSummary({ summary }) {
  return (
    <Card className="p-5 border" style={{ borderColor: C.border }} data-testid="pr-archive-summary">
      <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: C.plum }}>Summary & KPIs</div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <KpiRow k="Salaried" v={usd(summary.salaried_cents)} />
          <KpiRow k="Hourly" v={usd(summary.hourly_cents)} />
          <KpiRow k="Contractor" v={usd(summary.contractor_cents)} />
          <KpiRow k="Overseas Contractor" v={usd(summary.overseas_contractor_cents)} />
          <KpiRow k="Other admin" v={usd(summary.other_admin_cents)} />
          <div className="my-2 border-t" style={{ borderColor: C.border }} />
          <KpiRow k="Total payroll" v={usd(summary.total_payroll_cents)} big />
          <KpiRow k="SP income" v={usd(summary.sp_income_cents)} />
          <KpiRow k="Margin" v={usd(summary.margin_cents)} big tone={(summary.margin_cents || 0) >= 0 ? C.ok : C.rose} />
        </div>
        <div>
          <KpiRow k="Total units" v={int(summary.total_units)} />
          <KpiRow k="Payroll / unit" v={usd(summary.payroll_per_unit_cents)} />
          <KpiRow k="Income / unit" v={usd(summary.income_per_unit_cents)} />
          <KpiRow k="Margin %" v={pct(summary.margin_pct || 0)} />
          <KpiRow k="LC/NS Rate" v={pct(summary.lc_ns_rate_pct || 0)} />
        </div>
      </div>
    </Card>
  );
}

// ═══════════════ 3. KPI Dashboard (super_user only) ═══════════════
// Linear-regression best-fit trendline over an array of numbers.

export { ArchiveTab, MiniKpi };
